import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Coins, Save } from 'lucide-react';
import Layout from '../../components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// One row per configurable knob. `hint` is shown under the input so admins
// know the effect of each value at a glance.
const FIELD_GROUPS = [
  {
    title: 'Coin Value',
    description: 'How much one coin is worth when redeemed on an order.',
    fields: [
      { key: 'coin_value_inr', label: 'Coin value (₹)', min: 1, max: 1000, hint: 'Applies retroactively — all held balances change value when you save.' },
    ],
  },
  {
    title: 'Earning Limits (per user)',
    description: 'Caps for how many coins a user can earn. Prevents runaway earning from repeated approvals.',
    fields: [
      { key: 'monthly_earning_cap', label: 'Monthly earning cap', min: 0, max: 10000, hint: 'Hard block once exceeded, resets 1st of month.' },
      { key: 'daily_earning_cap', label: 'Daily earning cap', min: 0, max: 10000, hint: 'Soft block for the day, resets midnight UTC. Must be ≤ monthly cap.' },
    ],
  },
  {
    title: 'Abuse Detection Flags',
    description: 'Flag users for admin review without blocking. Adjust to tune sensitivity.',
    fields: [
      { key: 'rapid_earning_threshold', label: 'Rapid earning threshold (coins)', min: 1, max: 10000, hint: 'Flag if user earns more than this many coins within the window below.' },
      { key: 'rapid_earning_window_hours', label: 'Rapid earning window (hours)', min: 1, max: 168 },
      { key: 'burst_threshold', label: 'Burst threshold (transactions)', min: 1, max: 1000, hint: 'Flag if user gets this many coin transactions within the window below.' },
      { key: 'burst_window_minutes', label: 'Burst window (minutes)', min: 1, max: 1440 },
    ],
  },
  {
    title: 'Redemption Caps',
    description: 'Enforced server-side at order creation. Two independent caps: one limits how much of the ORDER value coins can cover, the other limits how much of the USER\'s coin balance they can spend in a single order.',
    fields: [
      { key: 'max_coins_per_order', label: 'Max coins per single order', min: 0, max: 100000, hint: '0 = no cap. Absolute ceiling on the number of coins redeemed in one payment.' },
      { key: 'max_redemption_pct_of_order', label: 'Max % of order value payable in coins  (order-side cap)', min: 0, max: 100, hint: '0 or 100 = no cap. Limits the discount side of an order. Example: 25 → on a ₹1,000 order, coins can pay at most ₹250 (= 25 coins @ ₹10), so the user must pay at least ₹750 real money.' },
      { key: 'max_pct_of_balance_per_order', label: 'Max % of user\'s balance spendable per order  (balance-side cap)', min: 0, max: 100, hint: '0 or 100 = no cap. Limits how much of a user\'s coin hoard can leave in one purchase. Example: 50 → if a user holds 100 coins, they can redeem at most 50 in one order (they keep 50 for future use).' },
      { key: 'max_redemption_per_month', label: 'Max coins redeemed per calendar month', min: 0, max: 100000, hint: '0 = no cap. Total coins a user can spend across ALL orders in a month. Resets 1st of each month.' },
    ],
  },
];

const ALL_KEYS = FIELD_GROUPS.flatMap(g => g.fields.map(f => f.key));

const CoinRewardsSettingsPage = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/reward-settings`);
      setSettings(r.data);
      const initial = {};
      ALL_KEYS.forEach(k => { initial[k] = r.data[k]; });
      setForm(initial);
    } catch (e) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const isDirty = settings && ALL_KEYS.some(k => Number(form[k]) !== Number(settings[k]));

  const changedFields = () => {
    const changed = {};
    ALL_KEYS.forEach(k => {
      if (Number(form[k]) !== Number(settings[k])) changed[k] = Number(form[k]);
    });
    return changed;
  };

  const handleSave = async () => {
    const changed = changedFields();
    if (Object.keys(changed).length === 0) {
      toast.info('Nothing to save');
      return;
    }
    // Warn if coin_value_inr changed — it affects held balances retroactively
    if ('coin_value_inr' in changed && settings?.coin_value_inr !== changed.coin_value_inr) {
      const ok = window.confirm(
        `Coin value will change from ₹${settings.coin_value_inr} to ₹${changed.coin_value_inr}. ` +
        `This retroactively changes the ₹-value of every coin already held by users. Continue?`
      );
      if (!ok) return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/reward-settings`, changed);
      toast.success('Settings saved');
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to save';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  };

  const handleField = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value === '' ? '' : Number(value) }));
  };

  return (
    <Layout>
      <div className="page-transition max-w-4xl mx-auto space-y-6">
        {/* Header — matches other admin-tools pages (see HealthMonitoringPage). */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/tools')} data-testid="coin-settings-back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Admin Tools
            </Button>
          </div>
        </div>

        {/* Page Title */}
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3 break-words">
            <Coins className="w-7 h-7 text-yellow-600" />
            Coin Rewards Settings
          </h1>
          <p className="text-gray-500 mt-1 break-words">
            Tune every coin knob live. Changes apply to all new transactions instantly. No redeploy needed.
          </p>
        </div>

        {loading ? (
          <div className="text-center text-gray-500 py-12">Loading settings…</div>
        ) : (
          <>
            {FIELD_GROUPS.map(group => (
              <Card key={group.title} className="mb-4">
                <CardHeader>
                  <CardTitle className="text-lg break-words">{group.title}</CardTitle>
                  <CardDescription className="break-words">{group.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {group.fields.map((f, idx) => (
                    <React.Fragment key={f.key}>
                      {idx > 0 && <Separator />}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
                        <Label htmlFor={f.key} className="md:pt-2 min-w-0 break-words">{f.label}</Label>
                        <div className="md:col-span-2 min-w-0">
                          <Input
                            id={f.key}
                            type="number"
                            min={f.min}
                            max={f.max}
                            value={form[f.key] ?? ''}
                            onChange={(e) => handleField(f.key, e.target.value)}
                            data-testid={`coin-setting-${f.key}`}
                          />
                          {f.hint && <p className="text-xs text-gray-500 mt-1 break-words">{f.hint}</p>}
                        </div>
                      </div>
                    </React.Fragment>
                  ))}
                </CardContent>
              </Card>
            ))}

            <div className="sticky bottom-4 bg-white border rounded-lg shadow-lg p-4 flex items-center justify-between gap-4 flex-wrap">
              <div className="text-sm text-gray-600 min-w-0">
                {settings?.updated_at && (
                  <>Last updated <strong>{new Date(settings.updated_at).toLocaleString()}</strong>{settings.updated_by ? ` by ${String(settings.updated_by).slice(0, 8)}…` : ''}</>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={load} disabled={saving} data-testid="coin-settings-reset-btn">Reset</Button>
                <Button onClick={handleSave} disabled={saving || !isDirty} className="bg-yellow-500 hover:bg-yellow-600 text-black" data-testid="coin-settings-save-btn">
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving…' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default CoinRewardsSettingsPage;
